import { ComponentInterface, getElement } from "@stencil/core";
import { componentHasAdoptedShadowStyle } from "../utils/shadow-dom-helper";

/**
 * Mandatory! Sets up default adopted stylesheets for variables and schemes
 * Since StencilJS version 4.22.1 Stencil is prepending CSSStyleSheets as soon as a scoped component is used in the parent components render function.
 * This forces us to add placeholder CSSStyleSheets for the scheme and the flexible variables.
 * Only then, we are able to know the index of the scheme CSSStyleSheet and to replace it as soon as a scheme is changed.
 * This is to adopted style sheet structure as we have it now:
 *
 * adoptedStyleSheets[n]: n style sheets per scoped component type <-- flexible amount
 * adoptedStyleSheets[adoptedStyleSheets.length - 3]: scheme style sheet <-- replaced after each change of scheme attribute
 * adoptedStyleSheets[adoptedStyleSheets.length - 2]: variable style sheet <-- each entry is added after setCssVariable() function call
 * adoptedStyleSheets[adoptedStyleSheets.length - 1]: base style sheet of component <-- stable
 *
 * The decorator method is called on componentWillLoad function call.
 * 1. placeholder style sheets are added to adoptedStyleSheets
 * 2. base style is appended to adoptedStyleSheets
 * 3. scoped style sheets are prepended to adoptedStyleSheets
 */
export function StyledComponent() {
  return (proto: ComponentInterface, _field: any) => {
    const { componentWillLoad } = proto;

    // Establishing transaction for component logging on componentWillLoad()
    proto.componentWillLoad = function () {
      const hostElement = getElement(this);

      // if component is shadowed and comes with adopted stylesheets
      if (componentHasAdoptedShadowStyle(hostElement)) {
        // ensure that slots for schemes and variables are already safed
        hostElement.shadowRoot.adoptedStyleSheets = [
          ...hostElement.shadowRoot.adoptedStyleSheets,
          new CSSStyleSheet(), // placeholder for scheme style sheet
          new CSSStyleSheet() // placeholder for variable style sheet
        ];
      }
      return componentWillLoad && componentWillLoad.call(this);
    };
  };
}
